import { useState, type ReactNode } from 'react'
import { ActualPopover } from '@/components/custom/ogsm/actual-popover'
import { ReviseTargetDialog } from '@/components/custom/ogsm/revise-target-dialog'
import { Typography } from '@/components/ui/typography'
import { isFedBySource } from '@/features/ogsm/actual-entry'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { formatTarget } from '@/features/ogsm/target-format'
import { formatDate } from '@/lib/format'
import type { OgsmBoardNode } from '@/types/api'

export function TargetCell({
  node,
  emptyLabel = OGSM_COPY.noGoal,
  layout = 'goal',
}: {
  node: OgsmBoardNode
  emptyLabel?: string
  /** goal: GOALS column. measure: a strategy row's measure (right-aligned, terse, no bar). inline: the parent card (one row, goal + actual, no bar, no %). */
  layout?: 'goal' | 'measure' | 'inline'
}) {
  const { session } = useBoardEdit()
  const [revising, setRevising] = useState(false)
  const current = formatTarget(
    node,
    { value: node.targetValue, text: node.targetText, date: node.targetDate },
    true
  )
  const original = node.revised
    ? formatTarget(
        node,
        {
          value: node.originalValue,
          text: node.originalText,
          date: node.originalDate,
        },
        false
      )
    : ''
  const actual = node.latestActual
    ? formatTarget(
        node,
        { value: node.latestActual.value, text: null, date: null },
        false
      )
    : ''
  const actualTitle = node.latestActual
    ? OGSM_COPY.asOf(formatDate(node.latestActual.asOfDate))
    : undefined
  const hasActual = node.latestActual !== null && actual !== ''
  // Same editor as the node menu's "Revise"; the menu is the keyboard path.
  const reviseOnDoubleClick = session.structureEditable
    ? () => setRevising(true)
    : undefined

  const caption = (label: string) => (
    <Typography as="span" variant="overline" className="text-primary/45">
      {label}
    </Typography>
  )
  const goalValue =
    current === '' ? (
      <Typography variant="muted">{emptyLabel}</Typography>
    ) : (
      <Typography
        as="p"
        variant="h6"
        className="font-semibold leading-snug tabular-nums"
        onDoubleClick={reviseOnDoubleClick}
      >
        {current}
      </Typography>
    )
  const was =
    original === '' ? null : (
      <Typography variant="body-sm" className="text-primary/60">
        {OGSM_COPY.was(original)}
      </Typography>
    )
  const actualValue = (
    <Typography
      as="span"
      variant="body"
      title={node.actualEditable ? undefined : actualTitle}
      className="font-semibold tabular-nums text-secondary/80"
    >
      {actual}
    </Typography>
  )
  // With no actual yet, the popover offers "Enter actual" in its place.
  const withActualEditor = (content?: ReactNode) =>
    node.actualEditable ? (
      <ActualPopover node={node} title={actualTitle}>
        {content}
      </ActualPopover>
    ) : (
      (content ?? null)
    )
  const reviseDialog = (session.structureEditable || revising) && (
    <ReviseTargetDialog
      node={node}
      open={revising}
      onOpenChange={setRevising}
    />
  )

  if (layout === 'measure') {
    return (
      <MeasureTarget
        current={current}
        emptyLabel={emptyLabel}
        was={original === '' ? '' : OGSM_COPY.was(original)}
        actualLine={withActualEditor(
          hasActual ? (
            <Typography
              as="span"
              variant="body"
              title={node.actualEditable ? undefined : actualTitle}
              className="mt-1 flex items-baseline justify-end gap-1.5 font-normal"
            >
              <Typography
                as="span"
                variant="overline"
                className="shrink-0 text-[9px] text-primary/45"
              >
                {OGSM_COPY.actualCaption}
              </Typography>
              <Typography
                as="span"
                variant="body"
                className="font-semibold tabular-nums text-secondary/80"
              >
                {actual}
              </Typography>
            </Typography>
          ) : undefined
        )}
        fedBySource={isFedBySource(node, session)}
        onDoubleClick={reviseOnDoubleClick}
      >
        {reviseDialog}
      </MeasureTarget>
    )
  }

  if (layout === 'inline') {
    return (
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-baseline gap-x-2">
          {caption(OGSM_COPY.goalCaption)}
          {goalValue}
          {was}
        </div>
        {withActualEditor(
          hasActual ? (
            <span className="flex items-baseline gap-x-2">
              {caption(OGSM_COPY.actualCaption)}
              {actualValue}
            </span>
          ) : undefined
        )}
        {reviseDialog}
      </div>
    )
  }

  const progress = node.progressPercent
  return (
    <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-2 gap-y-1">
      {caption(OGSM_COPY.goalCaption)}
      {goalValue}
      {was && <div className="col-start-2">{was}</div>}
      {hasActual ? (
        <>
          {caption(OGSM_COPY.actualCaption)}
          <div className="min-w-0">{withActualEditor(actualValue)}</div>
        </>
      ) : (
        node.actualEditable && (
          <div className="col-start-2">{withActualEditor()}</div>
        )
      )}
      {typeof progress === 'number' && (
        <div className="col-span-2 mt-1 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-primary/10">
            <div
              className="h-1 rounded-full bg-primary"
              style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
            />
          </div>
          <Typography
            variant="body-sm"
            className="shrink-0 text-primary/45 tabular-nums"
          >
            {progress}%
          </Typography>
        </div>
      )}
      {isFedBySource(node, session) && (
        <Typography variant="body-sm" className="col-span-2 text-primary/45">
          {OGSM_COPY.fedBySource}
        </Typography>
      )}
      {reviseDialog}
    </div>
  )
}

/** A strategy row's measure: right-aligned and terse, no progress bar. */
function MeasureTarget({
  current,
  emptyLabel,
  was,
  actualLine,
  fedBySource,
  onDoubleClick,
  children,
}: {
  current: string
  emptyLabel: string
  was: string
  actualLine: ReactNode
  fedBySource: boolean
  onDoubleClick?: () => void
  children?: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col items-end gap-1">
      <div className="flex items-baseline justify-end gap-1.5">
        {current === '' ? (
          <Typography variant="muted">{emptyLabel}</Typography>
        ) : (
          <Typography
            variant="body"
            className="tabular-nums"
            onDoubleClick={onDoubleClick}
          >
            {current}
          </Typography>
        )}
      </div>
      {was !== '' && (
        <Typography variant="body-sm" className="text-primary/60">
          {was}
        </Typography>
      )}
      {actualLine}
      {fedBySource && (
        <Typography variant="body-sm" className="text-primary/45">
          {OGSM_COPY.fedBySource}
        </Typography>
      )}
      {children}
    </div>
  )
}
